import ClassNames from "classnames";
import { EmblaCarouselType } from "embla-carousel";
import { FunctionComponent, useState } from "react";
import styles from "../stylesheets/components/highlights-layout-9.module.css";
import moduleBaseStyles from "../stylesheets/components/module-base.module.css";
import {
  ColorScheme,
  ContainerQueries,
  HighlightsAspectRatio,
  HighlightsItem,
  Language,
  TitleAndSubtitleTextAlign,
} from "../types/index.js";
import { css } from "../utils/emotion.js";
import CroppedImage from "./CroppedImage.js";
import HighlightHeading from "./HighlightHeading.js";
import ModuleHeadings from "./ModuleHeadings.js";
import Slider from "./Slider.js";
import SliderArrow from "./SliderArrow.js";

interface Props {
  title: string;
  subtitle: string;
  textAlign: TitleAndSubtitleTextAlign;
  isFirstOnPage: boolean;
  scheme: ColorScheme;
  items: HighlightsItem[];
  isPreview: boolean;
  queries: ContainerQueries;
  activeModuleId: string | undefined;
  transparentSliderArrowBackground: boolean;
  languageId: Language;
}

const HighlightsLayout9: FunctionComponent<Props> = ({
  title,
  subtitle,
  textAlign,
  isFirstOnPage,
  scheme,
  items,
  isPreview,
  queries,
  activeModuleId,
  transparentSliderArrowBackground,
  languageId,
}) => {
  const [slider, setSlider] = useState<EmblaCarouselType>();
  const hasHighlightsTitle = !!title;

  return (
    <div
      className={css({
        background: scheme.main.background,
        color: scheme.main.text,
      })}
    >
      <div
        className={ClassNames(
          moduleBaseStyles.Module__Wrapper,
          moduleBaseStyles["Module__Wrapper--small-padding"],
        )}
      >
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />
      </div>

      <div
        className={ClassNames(
          styles.HighlightsLayout9__Items,
          css({ "&::before": { background: scheme.main.separator } }),
        )}
      >
        <div
          className={ClassNames(
            styles.HighlightsLayout9__ItemsInner,
            moduleBaseStyles.Module__Wrapper,
          )}
        >
          <Slider
            containerClassName={styles.HighlightsLayout9__SliderContainer}
            slideClassName={styles.HighlightsLayout9__Slide}
            isPreview={isPreview}
            sliderEffect="slide"
            onSliderLoaded={setSlider}
            slidesToShow={1}
            activePreviewSlideId={activeModuleId}
            slideIds={items.map(({ id }) => id)}
          >
            {items.map(
              (
                { id, title, subtitle, picture, imageDetail, buttons },
                index,
              ) => {
                const prevItem =
                  items[(index === 0 ? items.length : index) - 1];
                const prevTitle = prevItem?.title || prevItem?.subtitle;
                const nextItem =
                  items[index === items.length - 1 ? 0 : index + 1];
                const nextTitle = nextItem?.title || nextItem?.subtitle;
                const backgroundAndTitleStyle = css({
                  background: scheme.main.background,
                  color: scheme.main.title,
                });

                return (
                  <article key={id} className={styles.HighlightsLayout9__Item}>
                    <div
                      className={ClassNames(
                        styles.HighlightsLayout9__NavTitle,
                        backgroundAndTitleStyle,
                      )}
                    >
                      {prevTitle}
                    </div>

                    <div className={styles.HighlightsLayout9__ItemMain}>
                      <div>
                        <CroppedImage
                          aspectRatio={getAspectRatio(queries)}
                          picture={picture}
                          width={1280}
                          detail={imageDetail}
                          sizes="(min-width: 640px) 50vw, (min-width: 1024px) 33vw, (min-width: 1600px) 500px, 100vw"
                        />
                      </div>

                      <HighlightHeading
                        className={ClassNames(
                          styles.HighlightsLayout9__ItemContent,
                          css({ background: scheme.main.background }),
                        )}
                        title={{
                          value: title,
                          className: ClassNames(
                            styles.HighlightsLayout9__ItemTitle,
                            css({ color: scheme.main.title }),
                          ),
                          children: title,
                        }}
                        subtitle={{
                          value: subtitle,
                          className: styles.HighlightsLayout9__ItemSubtitle,
                        }}
                        highlightIndex={index}
                        hasHighlightsTitle={hasHighlightsTitle}
                        isFirstOnPage={isFirstOnPage}
                      />

                      {!!buttons.length && (
                        <div className={styles.HighlightsLayout9__Buttons}>
                          {buttons}
                        </div>
                      )}
                    </div>

                    <div
                      className={ClassNames(
                        styles.HighlightsLayout9__NavTitle,
                        styles["HighlightsLayout9__NavTitle--right"],
                        backgroundAndTitleStyle,
                      )}
                    >
                      {nextTitle}
                    </div>
                  </article>
                );
              },
            )}
          </Slider>
          {items.length > 1 && (
            <div>
              <SliderArrow
                className={ClassNames(
                  styles.HighlightsLayout9__SliderArrow,
                  styles["HighlightsLayout9__SliderArrow--left"],
                )}
                iconClassName={styles.HighlightsLayout9__SliderArrowIcon}
                direction="left"
                onClick={() => slider?.scrollPrev()}
                scheme={scheme}
                transparentBackground={transparentSliderArrowBackground}
                languageId={languageId}
              />
              <SliderArrow
                className={ClassNames(
                  styles.HighlightsLayout9__SliderArrow,
                  styles["HighlightsLayout9__SliderArrow--right"],
                )}
                iconClassName={styles.HighlightsLayout9__SliderArrowIcon}
                direction="right"
                onClick={() => slider?.scrollNext()}
                scheme={scheme}
                transparentBackground={transparentSliderArrowBackground}
                languageId={languageId}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const getAspectRatio = (queries: ContainerQueries): HighlightsAspectRatio =>
  queries["Query--medium"] ? 1.5 : 0.8333;

export default HighlightsLayout9;
